function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useStyles from 'isomorphic-style-loader/useStyles';
import { FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import toNumber from 'lodash/toNumber';
import { useAction, useConversion, useStandards } from 'hooks';
import App from 'modules/App';
import Account from 'modules/Account';
import Locked from 'components/Locked';
import * as constants from '../../constants';
import messages from '../../messages';
import TargetsPicker from './TargetsPicker';
import styles from './GlucoseLevelTargets.pcss';
const GlucoseLevelTargets = ({ phiSet, patient, activeClinicMembership, isDisabled = false, withLocked = false })=>{
    useStyles(styles);
    const formName = constants.PATIENT_GLUCOSE_SETTINGS_FORM;
    const countrySettings = useSelector(Account.selectors.countrySettings);
    const setFormValue = useAction(App.actions.setFormValue, formName);
    const setFormValues = useAction(App.actions.setFormValues, formName);
    const [isLocked, setIsLocked] = useState(withLocked);
    const conversion = useConversion('bloodGlucoseConcentration');
    const { unit, displayPrecision } = conversion;
    const standards = useStandards();
    const formValues = App.hooks.useFormValues(formName);
    useEffect(()=>{
        setFormValues({
            critical_highThreshold: conversion.toDisplay(standards.critical.highThreshold),
            critical_lowThreshold: conversion.toDisplay(standards.critical.lowThreshold),
            postMeal_highThreshold: conversion.toDisplay(standards.postMeal.highThreshold),
            postMeal_lowThreshold: conversion.toDisplay(standards.postMeal.lowThreshold),
            preMeal_highThreshold: conversion.toDisplay(standards.preMeal.highThreshold),
            preMeal_lowThreshold: conversion.toDisplay(standards.preMeal.lowThreshold)
        });
    }, [
        standards
    ]);
    const defaultValuesSliders = useMemo(()=>({
            valueMin: 0,
            valueMax: unit === 'MMOL_L' ? 20 : 350,
            valueStep: unit === 'MMOL_L' ? 2.5 : 50,
            valueDragStep: unit === 'MMOL_L' ? 0.1 : 1
        }), [
        unit
    ]);
    //--------------------------------------------------------------------------------------------------------------------
    const onSetValue = useCallback((input)=>{
        const { id, value } = input;
        const roundedValue = isNumber(value) ? toNumber(value.toFixed(displayPrecision)) : value;
        setFormValue({
            id,
            value: roundedValue
        });
    }, [
        displayPrecision,
        setFormValue
    ]);
    const onLockedClick = useCallback(()=>{
        setIsLocked((prevIsLocked)=>!prevIsLocked);
    }, []);
    //--------------------------------------------------------------------------------------------------------------------
    const pickerProps = _object_spread({
        onRangeChange: onSetValue,
        onTimeChange: setFormValue,
        formValues,
        conversion
    }, defaultValuesSliders);
    const diabetesTypeMessage = App.messages.diabetesTypes[get(phiSet, 'diabetesType', 'NoResponse')];
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            !isDisabled && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("h1", {
                        className: styles.headerTargetRanges,
                        children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread_props(_object_spread({}, messages.headers.targetRanges), {
                            values: {
                                name: /*#__PURE__*/ _jsx("b", {
                                    children: `${patient.firstName} ${patient.lastName}`
                                }),
                                diabetesType: /*#__PURE__*/ _jsx("b", {
                                    children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, diabetesTypeMessage))
                                })
                            }
                        }))
                    }),
                    withLocked && /*#__PURE__*/ _jsx("div", {
                        className: styles.locked,
                        children: /*#__PURE__*/ _jsx(Locked, {
                            isLocked: isLocked,
                            onClick: onLockedClick
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "row",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "col-4",
                        children: /*#__PURE__*/ _jsx(TargetsPicker, _object_spread_props(_object_spread({
                            targetType: "preMeal"
                        }, pickerProps), {
                            locked: isDisabled || isLocked,
                            countrySettings: countrySettings,
                            conversion: conversion,
                            activeClinicMembership: activeClinicMembership,
                            phiSet: phiSet,
                            withoutOrder: true
                        }))
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "col-4",
                        children: /*#__PURE__*/ _jsx(TargetsPicker, _object_spread_props(_object_spread({
                            targetType: "postMeal"
                        }, pickerProps), {
                            locked: isDisabled || isLocked,
                            countrySettings: countrySettings,
                            conversion: conversion,
                            activeClinicMembership: activeClinicMembership,
                            phiSet: phiSet
                        }))
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "col-4",
                        children: /*#__PURE__*/ _jsx(TargetsPicker, _object_spread_props(_object_spread({
                            targetType: "critical"
                        }, pickerProps), {
                            locked: isDisabled || isLocked,
                            countrySettings: countrySettings,
                            conversion: conversion,
                            activeClinicMembership: activeClinicMembership,
                            phiSet: phiSet
                        }))
                    })
                ]
            })
        ]
    });
};
export default /*#__PURE__*/ memo(GlucoseLevelTargets);
